import React from 'react';

import parseGraphQlErrorTypeFromErrorPayload, { GraphQlErrorType } from './bet-error-type';
import { showFailureModal, closeModal } from '../components/modal';
import styled from 'styled-components';
import pubsub from './pubsub';
import { formatMoneyWithSymbol } from './money-formatter';
import { unitsValueMoney } from './money';
import { BetSlipMessageType, PriceChangeMessage } from './pubsub';
import { IBettingError, IParsedSingleBets, IPlaceBetErrorHandlerProps, IParsedBet, IParsedLeg } from '../types';
import { getDeviceInfo } from '../retail-config/device/operations';
import flatMap from './flat-map';

const Span = styled.span<{ bold: boolean; marginTop: string; displayBlock: boolean }>`
  ${({ marginTop }) => `margin-top: ${marginTop};`}
  ${({ bold }) => bold && `font-weight: 600;`}
  ${({ displayBlock }) => displayBlock && `display: block;`}
`;

type ParsedBetFromError = {
  index: number;
} & Pick<IParsedLeg, 'eventId' | 'marketId' | 'selectionId'> &
  Pick<IParsedBet, 'type' | 'stakePerLine'> &
  Pick<IBettingError, 'details' | 'message'>;

export const parseBetFromError = (
  { pointer, message, details }: IBettingError,
  operationVariables: IParsedSingleBets
): ParsedBetFromError => {
  const splitPointer = (pointer as string).split('/');
  const betIndex = parseInt(splitPointer[2], 10);
  const legsIndex = parseInt(splitPointer[4], 10) || 0;
  const { stakePerLine, type } = operationVariables.bets[betIndex];
  const { eventId, marketId, selectionId } = operationVariables.bets[betIndex].legs[legsIndex];

  return {
    index: legsIndex,
    eventId,
    marketId,
    selectionId,
    details,
    message,
    stakePerLine,
    type
  };
};

export const parseBetsFromErrors = (
  errors: IBettingError[] | IBettingError,
  operationVariables: IParsedSingleBets
): ParsedBetFromError[] => {
  return (Array.isArray(errors) ? errors : [errors]).map(error => parseBetFromError(error, operationVariables));
};

const mergeErrorPerSelection = (errors: Pick<ParsedBetFromError, 'selectionId' | 'details'>[]): PriceChangeMessage =>
  errors.reduce((mergedErrors, { selectionId, details }) => {
    const existingError = mergedErrors.find(e => e.selectionId === selectionId);
    if (existingError) {
      existingError.details = { ...existingError.details, ...details };
      return mergedErrors;
    }
    mergedErrors.push({ selectionId, details });
    return mergedErrors;
  }, Array<PriceChangeMessage[number]>());

export const insufficientFunds = ({ error }: IPlaceBetErrorHandlerProps) => {
  const currentBalance = formatMoneyWithSymbol(unitsValueMoney(error.exception.details[0].details.currentAmount), '$');
  const requiredBalance = formatMoneyWithSymbol(
    unitsValueMoney(error.exception.details[0].details.requiredAmount),
    '$'
  );
  showFailureModal({
    title: 'Insufficient Funds',
    content: (
      <Span bold={true} marginTop="20px" displayBlock={true}>
        {`Insufficient funds. Playable balance is ${currentBalance}, but required ${requiredBalance}.`}
      </Span>
    ),
    context: 'To place the bet you will need to insert funds.'
  });
};

export const accountSuspended = () => {
  showFailureModal({
    title: 'Unable to Place Bet',
    context: 'This account is currently suspended. Please contact customer support.'
  });
};

export const betOfferExpired = () => {
  showFailureModal({
    title: 'Bet Offer Expired',
    context: 'The bet offer has now expired. Place the bet again.'
  });
};
const formatMoney = (amount: number) => formatMoneyWithSymbol(unitsValueMoney(amount), '$');
const getWagerText = (textList: string[]) => `wager${textList.length > 1 ? 's' : ''}`;
const getRespectivelyText = (textList: string[]) => `${textList.length > 1 ? ' respectively' : ''}`;
export const maxPotentialPayoutExceeded = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();

  const deviceInfo = getDeviceInfo();
  const maxPotentialPayoutExceededDetails = props.error.exception.details.filter(
    detail => detail.code === 'betErrors.maxPotentialPayoutExceeded'
  );
  const maxPotentialPayoutChannelLimit =
    maxPotentialPayoutExceededDetails[0].details['max-potential-payout-channel-limit'];

  const maximumAllowedStakes = maxPotentialPayoutExceededDetails.map(detail =>
    formatMoney(detail.details['maximum-allowed-stake'])
  );

  const maximumAllowedStakesText = maximumAllowedStakes.join(', ');

  const stakesPerLine = flatMap(maxPotentialPayoutExceededDetails, exceptionDetails =>
    parseBetsFromErrors(exceptionDetails, props.operationVariables)
  ).map(betError => formatMoney(betError.stakePerLine));

  const stakesPerLineText = stakesPerLine.join(', ');

  const wagerText = getWagerText(stakesPerLine);
  const respectivelyText = getRespectivelyText(stakesPerLine);

  const kioskErrorMessage = `The ${wagerText} you are attempting to place would exceed the maximum payout limit of ${formatMoney(
    maxPotentialPayoutChannelLimit
  )}. Change the ${stakesPerLineText} ${wagerText} to ${maximumAllowedStakesText}${respectivelyText} to place this bet now. Alternatively place the ${wagerText} with a Ticket Writer.`;

  const posErrorMessage = `The ${wagerText} you are attempting to place would exceed the maximum payout limit. Change the ${stakesPerLineText} ${wagerText} to ${maximumAllowedStakesText}${respectivelyText} to place this bet now. Alternatively to place a larger bet call a Supervisor.`;

  const errorMessage = deviceInfo.slotId.type === 'POS' ? posErrorMessage : kioskErrorMessage;

  pubsub.publishMessage('betslip.errors', {
    errorMessage,
    betsWithErrors: []
  });
};

export const maxWagerExceeded = () => {
  showFailureModal({
    title: 'Unable to Place Bet',
    context: 'The maximum wager has been exceeded. Reduce your wager and try again.'
  });
};

export const relatedContingency = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();
  pubsub.publishMessage('betslip.errors', {
    errorMessage:
      'Highlighted selections cannot be placed together as a bet. Remove related selections to place this bet.',
    betsWithErrors: parseBetsFromErrors(props.error.exception.details[0], props.operationVariables)
  });
};

export const betTypeUnacceptable = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();
  pubsub.publishMessage('betslip.errors', {
    errorMessage:
      'Highlighted selections cannot be placed in this bet type. Remove one of the selections from the bet slip.',
    betsWithErrors: parseBetsFromErrors(props.error.exception.details[0], props.operationVariables)
  });
};

export const maxStakeExceeded = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();

  const maxStake = props.error.exception.details[0].details.maxStake;

  pubsub.publishMessage('betslip.errors', {
    errorMessage: `The maximum wager has been exceeded. Change the wager to ${formatMoneyWithSymbol(
      unitsValueMoney(maxStake),
      '$'
    )} or less.`,
    betsWithErrors: parseBetsFromErrors(props.error.exception.details[0], props.operationVariables)
  });
};

export const minWagerNotMet = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();
  pubsub.publishMessage('betslip.errors', {
    errorMessage: `The minimum wager has not been met. Change the wager to ${formatMoneyWithSymbol(
      unitsValueMoney(props.error.exception.details[0].details.minTotalStake),
      '$'
    )} or more.`,
    betsWithErrors: parseBetsFromErrors(props.error.exception.details, props.operationVariables)
  });
};

export const maxAllowedStake = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();

  const maxAllowedStakeDetails = props.error.exception.details.filter(
    detail => detail.code === 'betErrors.generic' && detail.details['maximum-allowed-stake']
  );

  const maximumAllowedStakes = maxAllowedStakeDetails.map(detail =>
    formatMoney(detail.details['maximum-allowed-stake'])
  );

  const stakesPerLine = flatMap(maxAllowedStakeDetails, exceptionDetails =>
    parseBetsFromErrors(exceptionDetails, props.operationVariables)
  ).map(betError => formatMoney(betError.stakePerLine));

  const stakesPerLineText = stakesPerLine.join(', ');

  const wagerText = getWagerText(stakesPerLine);
  const respectivelyText = getRespectivelyText(stakesPerLine);

  const maximumAllowedStakesText = maximumAllowedStakes.join(', ');

  const errorMessage = `The ${wagerText} you are attempting to place would exceed the maximum payout limit. Change the ${stakesPerLineText} ${wagerText} to ${maximumAllowedStakesText}${respectivelyText} to place this bet now.`;

  pubsub.publishMessage('betslip.errors', {
    errorMessage,
    betsWithErrors: []
  });
};

export const priceChange = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();
  const data = mergeErrorPerSelection(parseBetsFromErrors(props.error.exception.details, props.operationVariables));
  pubsub.publishMessage<BetSlipMessageType, PriceChangeMessage>('BET_SLIP.PRICE_CHANGE_FROM_BET_PLACEMENT_ERROR', data);
};

export const suspended = (props: IPlaceBetErrorHandlerProps) => {
  closeModal();
  pubsub.publishMessage('betslip.suspension', {
    betsWithErrors: parseBetsFromErrors(props.error.exception.details, props.operationVariables)
  });
};

const placeBetErrorHandlers = {
  [GraphQlErrorType.InsufficientFunds]: insufficientFunds,
  [GraphQlErrorType.AccountSuspended]: accountSuspended,
  [GraphQlErrorType.BetOfferExpired]: betOfferExpired,
  [GraphQlErrorType.MaxWagerExceeded]: maxWagerExceeded,
  [GraphQlErrorType.RelatedContingency]: relatedContingency,
  [GraphQlErrorType.BetTypeUnacceptable]: betTypeUnacceptable,
  [GraphQlErrorType.MaxStakeExceeded]: maxStakeExceeded,
  [GraphQlErrorType.MinWagerNotMet]: minWagerNotMet,
  [GraphQlErrorType.PriceChange]: priceChange,
  [GraphQlErrorType.EntitySuspended]: suspended,
  [GraphQlErrorType.MaxPotentialPayoutExceeded]: maxPotentialPayoutExceeded,
  [GraphQlErrorType.MaxAllowedStake]: maxAllowedStake,
  [GraphQlErrorType.Default]: () => showFailureModal({ title: 'Bet Placed Failure', context: 'Unable to place bet' })
};

export const handlePlaceBetUnprocessableEntity = (error: any, operationVariables: any) => {
  const graphQlErrorType = parseGraphQlErrorTypeFromErrorPayload(error);
  if (placeBetErrorHandlers[graphQlErrorType]) {
    placeBetErrorHandlers[graphQlErrorType]({ error, operationVariables });
  }
};

export const forTestOnly = { mergeErrorPerSelection };
